/**
 * Created by spepa on 10.04.2023
 */

const Achievements = function () {
    cleverapps.EventEmitter.call(this);

    this.tasks = [];
    this.pending = {};
    this.attention = false;

    this.load();

    cleverapps.eventBus.on("taskEvent", this.onTaskEvent.bind(this));
    cleverapps.eventBus.on("mapEvent", this.onMapEvent.bind(this));
    cleverapps.eventBus.on("unitOpened", (unit) => {
        this.onMapEvent(Map2d.OPEN_UNIT, { unit });
    }, this);
    cleverapps.eventBus.on("questFinished", (quest) => {
        this.onQuestFinished(quest);
    });

    this.on("complete", this.onComplete.bind(this));
};

Achievements.prototype = Object.create(cleverapps.EventEmitter.prototype);
Achievements.prototype.constructor = Achievements;

Achievements.prototype.load = function (serverData) {
    let value = cleverapps.dataLoader.load(DataLoader.TYPES.ACHIEVEMENTS);
    if (serverData !== undefined) {
        value = serverData;
    }

    if (cleverapps.config.debugMode) {
        const testUnknown = cleverapps.dataLoader.load(DataLoader.TYPES.UNKNOWN_ACHIEVEMENT_TEST) || [];
        testUnknown.forEach((id) => {
            const index = AchievementsConfig.findIndex((config) => config.id === id);
            if (index !== -1) {
                AchievementsConfig.splice(index, 1);
            }
        });
    }

    const completed = Achievements.parseValue(value);
    const knownIds = new Set(AchievementsConfig.map((config) => config.id));
    this.unknownIds = completed.filter((id) => !knownIds.has(id));

    this.wantsPastCheck = true;
    this.tasks = AchievementsConfig.map((config) => new Achievement(config, completed.includes(config.id)));

    const attention = cleverapps.dataLoader.load(DataLoader.TYPES.ACHIEVEMENTS_ATTENTION);
    this.setAttention(attention);
};

Achievements.parseValue = function (value) {
    if (!value) {
        return [];
    }
    if (typeof value === "string") {
        return cleverapps.fromBitBase64(value);
    }
    return cleverapps.fromBit(value);
};

Achievements.prototype.getInfo = function () {
    const completed = [];
    this.tasks.forEach((task) => {
        if (task.isComplete()) {
            completed.push(task.config.id);
        }
    });
    return cleverapps.toBitBase64(completed.concat(this.unknownIds || []));
};

Achievements.prototype.save = function (updateServer) {
    cleverapps.dataLoader.save(DataLoader.TYPES.ACHIEVEMENTS, this.getInfo());

    if (updateServer !== false) {
        connector.synchronizer.addSendTask("achievements");
    }
};

Achievements.prototype.grant = function (id) {
    const task = this.tasks.find((task) => task.config.id === id);
    if (task) {
        task.complete();
    }
};

Achievements.prototype.onComplete = function (config) {
    if (config.expedition) {
        cleverapps.metrics.logEvent(cleverapps.EVENTS.LOCATION.COMPLETED, { location: config.expedition });
        Map2dLocation.LogDay(config.expedition, cleverapps.EVENTS.LOCATION.DAY_COMPLETED);
    }

    if (this.listWindowAchievements().some((windowConfig) => windowConfig.id === config.id)) {
        this.setAttention(true);
    }
};

Achievements.prototype.setAttention = function (attention) {
    attention = Boolean(attention);

    if (this.attention === attention) {
        return;
    }

    this.attention = attention;
    cleverapps.dataLoader.save(DataLoader.TYPES.ACHIEVEMENTS_ATTENTION, attention);

    this.trigger("changeAttention");
};

Achievements.prototype.hasAttention = function () {
    return this.attention;
};

Achievements.prototype.isGathered = function (id) {
    const task = this.tasks.find((task) => task.config.id === id);
    return task && task.isComplete();
};

Achievements.prototype.revoke = function (id) {
    const task = this.tasks.find((task) => task.config.id === id);
    if (task) {
        task.completed = false;
        this.save();
    }
};

Achievements.prototype.canGiveAchievement = function (config) {
    if (!config || !config.donors || config.donors.length === 0) {
        return false;
    }
    if (this.isGathered(config.id)) {
        return false;
    }
    return config.donors.every((donor) => Families[donor] && cleverapps.unitsLibrary.isOpened(UnitsLibrary.LastStage(donor)));
};

// a game with landmarks shows its achievements on the map: no shelves, no reward window
Achievements.hasLandmarks = function () {
    return Boolean(typeof Map2dLocation !== "undefined" && Map2dLocation.currentLocation && Map2dLocation.currentLocation.landmarks);
};

Achievements.getName = function (config) {
    if (!cleverapps.achievements) {
        return "";
    }
    const task = cleverapps.achievements.tasks.find((t) => t.config.id === config.id);
    return task ? task.getName() : "";
};

Achievements.getDescription = function (config) {
    if (!cleverapps.achievements) {
        return "";
    }
    const task = cleverapps.achievements.tasks.find((t) => t.config.id === config.id);
    return task ? task.getDescription() : "";
};

Achievements.getIcon = function (config, options) {
    if (!cleverapps.achievements) {
        return new cc.Node();
    }
    const task = cleverapps.achievements.tasks.find((t) => t.config.id === config.id);
    if (task) {
        return task.getIcon(options);
    }
    return new cc.Node();
};

Achievements.listGatheredUnits = function (code) {
    if (!cleverapps.achievements) {
        return [];
    }
    return AchievementsConfig.listUnitAchievements()
        .filter((config) => config.unit.code === code && cleverapps.achievements.isGathered(config.id))
        .map((config) => config.unit);
};

Achievements.prototype.checkPastFeats = function () {
    this.wantsPastCheck = false;

    if (cleverapps.config.type === "match3") {
        this.onTaskEvent("PASS_LEVEL");
        this.onTaskEvent("UPGRADE_HERO");
    } else if (cleverapps.config.type === "merge") {
        this.onTaskEvent("LEVEL_UP");
        this.onMapEvent(Map2d.OPEN_UNIT);
    }
};

Achievements.prototype.listByActiveExpeditions = function () {
    const configs = AchievementsConfig.listByExpedition(cleverapps.meta.selectedLocationId());

    return configs.filter((config) => {
        if (config.fog && !Map2d.currentMap.fogs.isOpened(config.fog)) {
            return false;
        }
        if (config.target && !cleverapps.unitsLibrary.isOpened(config.target)) {
            return false;
        }
        if (!config.donors || config.donors.length === 0) {
            return false;
        }
        if (config.feature && !cleverapps.eventManager.isActive(config.feature)) {
            return false;
        }
        return !this.isGathered(config.id);
    });
};

Achievements.prototype.onMapEvent = function (type, options) {
    this.tasks.forEach((task) => {
        if (task.type === type) {
            switch (type) {
                case Map2d.MERGE:
                    if (task.config.amount <= options.affected.length) {
                        task.complete();
                    }
                    break;
                case Map2d.OPEN_UNIT:
                    if (cleverapps.unitsLibrary.isOpened(task.config.unit)) {
                        task.complete();
                    }
                    break;
                case Map2d.BUILD:
                    if (task.config.castle && options.affected.isCastle()) {
                        task.complete();
                    }
                    break;
                default:
                    task.complete();
                    break;
            }
        }
    });
};

Achievements.prototype.onQuestFinished = function (quest) {
    this.tasks.forEach((task) => {
        if (task.config.donorQuest === quest.id && task.config.expedition === cleverapps.meta.selectedLocationId() && !task.isComplete()) {
            task.complete();

            if (!Achievements.hasLandmarks() && typeof AchievementRewardWindow !== "undefined") {
                cleverapps.focusManager.display({
                    focus: "AchievementRewardWindow",
                    action: function (f) {
                        new AchievementRewardWindow(task.config);
                        cleverapps.focusManager.onceNoWindowsListener = f;
                    }
                });
            }
        }
    });
};

// the not yet gathered visit achievement of the selected location; granted by the visitLocationAchievement placement
Achievements.prototype.findVisitLocationTask = function () {
    const locationId = cleverapps.meta.selectedLocationId();
    return this.tasks.find((task) => task.type === Achievements.VISIT_LOCATION && task.config.location === locationId && !task.isComplete());
};

Achievements.prototype.onTaskEvent = function (type) {
    this.tasks.forEach((task) => {
        if (task.type === type) {
            switch (type) {
                case "PASS_LEVEL":
                    if (cleverapps.user.getHumanReadableNumber() >= task.config.level) {
                        task.complete();
                    }
                    break;
                case "LEVEL_UP":
                    if (task.config.level <= cleverapps.user.currentPhysicalLevel.level) {
                        task.complete();
                    }
                    break;
                case "UPGRADE_HERO":
                    if (task.config.allOpen) {
                        if (Heroes.AVAILABLE_HEROES.every((color) => match3.heroes.getLevel(color) >= 1)) {
                            task.complete();
                        }
                    } else if (match3.heroes.getLevel(task.config.hero.color) >= task.config.hero.level) {
                        task.complete();
                    }
                    break;
                default:
                    task.complete();
                    break;
            }
        }
    });
};

Achievements.prototype.listCompleted = function () {
    return this.tasks.filter((task) => task.isComplete());
};

Achievements.prototype.findDonorConfig = function (donor) {
    const configs = AchievementsConfig.listByExpedition(cleverapps.meta.selectedLocationId());

    return configs.find((config) => {
        if (config.fog && !Map2d.currentMap.fogs.isOpened(config.fog)) {
            return false;
        }
        if (config.target && !cleverapps.unitsLibrary.isOpened(config.target)) {
            return false;
        }
        return config.donors && config.donors.includes(donor.code);
    });
};

Achievements.prototype.isActiveDonor = function (donor) {
    const config = this.findDonorConfig(donor);
    if (!config) {
        return false;
    }
    if (config.feature && !cleverapps.eventManager.isActive(config.feature)) {
        return false;
    }
    if (this.isGathered(config.id)) {
        return false;
    }
    return !cleverapps.unitsLibrary.isOpened(UnitsLibrary.LastStage(donor.code));
};

Achievements.prototype.getTimeLeft = function (config) {
    if (config.feature) {
        const event = cleverapps.eventManager.getFeatureEvent(config.feature);
        return Math.max(0, event && event.getTimeLeft() || 0);
    }
    return cleverapps.meta.getSelectedLocation().getTimeLeft();
};

Achievements.prototype.calcProgress = function (config, shown) {
    const pending = shown ? this.pending[Unit.GetKey(config.unit)] || 0 : 0;

    if (config.donors.length === 1) {
        const donorCode = config.donors[0];
        const opened = cleverapps.unitsLibrary.getLastOpenStage(donorCode);
        const total = UnitsLibrary.ListByCode(donorCode).length;
        return opened === undefined ? 0 : Math.round((opened - pending + 1) * 100 / total);
    }

    const totalDonors = config.donors.length;
    const openedDonors = config.donors.filter((donorCode) => cleverapps.unitsLibrary.isOpened(UnitsLibrary.LastStage(donorCode))).length;
    return Math.round((openedDonors - pending) * 100 / totalDonors);
};

Achievements.prototype.increasePendingProgress = function (donor) {
    const config = this.findDonorConfig(donor);
    if (!config) {
        return;
    }

    if (config.donors.length === 1 ? donor.stage === cleverapps.unitsLibrary.getLastOpenStage(donor.code) : donor.stage === UnitsLibrary.LastStage(donor.code).stage) {
        const key = Unit.GetKey(config.unit);
        this.pending[key] = (this.pending[key] || 0) + 1;
        if (typeof AchievementDonorIcon !== "undefined") {
            cleverapps.sideBar.resetByClassName(AchievementDonorIcon);
        }
    }
};

Achievements.prototype.getPendingProgress = function (config) {
    const key = Unit.GetKey(config.unit);
    return this.pending[key] || 0;
};

Achievements.prototype.clearPendingProgress = function (config) {
    this.pending[Unit.GetKey(config.unit)] = 0;
};

Achievements.prototype.isAllGathered = function () {
    const configs = AchievementsConfig.listByExpedition(cleverapps.meta.selectedLocationId());
    return configs.length > 0 && configs.every((config) => this.isGathered(config.id));
};

// the shelves hold the achievements marked onShelf first, then the unit (landmark) ones
Achievements.prototype.listWindowAchievements = function () {
    const activeLocations = cleverapps.meta.listActiveLocations();
    const isAvailable = (config) => !config.available || cleverapps.user.checkAvailable(config.available);

    const onShelf = AchievementsConfig.filter((config) => config.onShelf && isAvailable(config)
        && (!config.location || this.isGathered(config.id) || activeLocations.includes(config.location)));

    const units = AchievementsConfig.listUnitAchievements().filter((config) => {
        if (!config.expedition || !isAvailable(config)) {
            return false;
        }
        return (config.donors && config.donors.length > 0 || config.donorQuest || this.isGathered(config.id))
            && (this.isGathered(config.id) || activeLocations.includes(config.expedition));
    });

    return onShelf.concat(units);
};

Achievements.getForce = function (expedition) {
    const config = AchievementsConfig.findByExpedition(expedition);
    return config && config.force;
};

Achievements.clearForce = function (expedition) {
    const force = Achievements.getForce(expedition);
    if (force) {
        cleverapps.forces.clearForce(force);
    }
};

Achievements.VISIT_LOCATION = "visitLocation";

cleverapps.InitByFeature["achievements"] = function () {
    cleverapps.achievements = new Achievements();
};
